import type { CollisionDetection, DragEndEvent, Modifier } from '@dnd-kit/core';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AccessLevel,
  colors,
  darkThemeSelector,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  fade,
  palette,
  styled,
  UtilityControl,
} from '@meterup/atto';
import { memo, useCallback, useMemo, useState } from 'react';

import type { SharedColumnDef } from '../createColumnBuilder';
import type { AutoCollectionStateInterface } from '../utils';
import {
  buildColumnMenu,
  COLUMN_MENU_OPERATOR,
  COLUMN_MENU_ROOT,
  columnMenuItemColumnIDs,
  findSortableOver,
  moveColumnIDs,
} from './columnMenu';

interface TableColumnsVisibilityProps<RowType = any> {
  columns: ColumnDef<RowType, any>[];
  columnVisibility: Record<string, boolean>;
  setColumnVisibility: (value: Record<string, boolean>) => void;
  columnOrder?: string[];
  onResetColumnOrder?: () => void;
  defaultColumnOrder?: string[];
}

// New enhanced interface that works with TableState
interface TableColumnsEnhancedProps<RowType = any> {
  columns: SharedColumnDef<RowType, any>[];
  tableState: AutoCollectionStateInterface<RowType>;
}

const SORTABLE_TRANSITION = { duration: 150, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' };

export const DEFAULT_COLUMN_GROUP_NAME = 'Columns';
export const OPERATOR_COLUMN_GROUP_NAME = 'Operator';

const TableColumnsDraggableHandle = styled(UtilityControl, {
  cursor: 'grab',

  variants: {
    isDragging: {
      true: {
        cursor: 'grabbing',
      },
      false: {},
    },
  },
});
TableColumnsDraggableHandle.displayName = 'TableColumnsDraggableHandle';

// Matches the right inset of `TableColumnsDraggableContainer` so group and column handles line up.
const TableColumnsGroupHandle = styled(TableColumnsDraggableHandle, {
  marginRight: '$4',
});
TableColumnsGroupHandle.displayName = 'TableColumnsGroupHandle';

const TableColumnsDraggableCheckbox = styled(DropdownMenuCheckboxItem, {
  width: '100%',
  pointerEvents: '$$tableColumnsDraggableCheckboxPointerEvents',
});
TableColumnsDraggableCheckbox.displayName = 'TableColumnsDraggableCheckbox';

const TableColumnsDraggableContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  gap: '$4',
  paddingRight: '$4',
  borderRadius: '$6',
  userSelect: 'none',
  cursor: 'grab',

  variants: {
    isDragging: {
      true: {
        opacity: 0.8,
        zIndex: 2,
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        cursor: 'grabbing',

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
      false: {
        zIndex: 1,
        opacity: 1,
      },
    },
  },
});
TableColumnsDraggableContainer.displayName = 'TableColumnsDraggableContainer';

const TableColumnsGroup = styled(DropdownMenuGroup, {
  variants: {
    isDragging: {
      true: {
        position: 'relative',
        $$tableColumnsDraggableCheckboxPointerEvents: 'none',

        '&::before': {
          content: '',
          position: 'absolute',
          inset: '$4',
          background: fade(palette.bgBrandLight, 0.2),
          strokeAll: fade(palette.strokeBrandLight, 0.3),
          borderRadius: '$6',
          pointerEvents: 'none',
          zIndex: 1,

          [darkThemeSelector]: {
            background: fade(palette.bgBrandDark, 0.2),
            strokeAll: fade(palette.strokeBrandDark, 0.3),
          },
        },
      },
      false: {},
    },
  },
});
TableColumnsGroup.displayName = 'TableColumnsGroup';

// Scrolls the columns so the reset group below stays in view.
const TableColumnsScrollArea = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'auto',
  strokeBottom: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },
});
TableColumnsScrollArea.displayName = 'TableColumnsScrollArea';

const TableColumnsDraggable = memo(
  ({
    column,
    columnVisibility,
    onVisibilityChange,
    isDragging,
  }: {
    column: ColumnDef<any, any>;
    columnVisibility: Record<string, boolean>;
    onVisibilityChange: (columnId: string, checked: boolean) => void;
    isDragging: boolean;
  }) => {
    const isDraggable = !column.meta?.fixedPosition;
    const isAlwaysVisible = column.meta?.alwaysVisible === true;

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: column.id!,
      disabled: !isDraggable,
      animateLayoutChanges: () => false, // Disable layout animations to prevent flicker
      transition: SORTABLE_TRANSITION,
    });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
    };

    return (
      <TableColumnsDraggableContainer ref={setNodeRef} style={style} isDragging={isDragging}>
        <TableColumnsDraggableCheckbox
          checked={isAlwaysVisible ? true : columnVisibility[column.id!] ?? true}
          disabled={isAlwaysVisible}
          accessLevel={column.meta?.accessLevel}
          onCheckedChange={(checked) => {
            if (!isAlwaysVisible) {
              onVisibilityChange(column.id!, checked);
            }
          }}
          closeOnClick={false}
        >
          {column.meta?.label ?? (typeof column.header === 'string' ? column.header : column.id)}
        </TableColumnsDraggableCheckbox>
        {isDraggable && (
          <TableColumnsDraggableHandle
            {...attributes}
            {...listeners}
            active={isDragging}
            icon="slide-vertical"
            isDragging={isDragging}
            label="Reorder"
            variant={isDragging ? 'brand' : 'neutral'}
          />
        )}
      </TableColumnsDraggableContainer>
    );
  },
);

const TableColumnsDraggableGroupContainer = styled('div', {
  borderRadius: '$8',

  variants: {
    isDragging: {
      true: {
        position: 'relative',
        zIndex: 2,
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
      false: {},
    },
  },
});
TableColumnsDraggableGroupContainer.displayName = 'TableColumnsDraggableGroupContainer';

const TableColumnsDraggableGroupLabel = styled('span', {
  variants: {
    isDragging: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
TableColumnsDraggableGroupLabel.displayName = 'TableColumnsDraggableGroupLabel';

const TableColumnsDraggableGroup = memo(
  ({
    id,
    label,
    isDraggable,
    isDragging,
    isColumnDragging,
    children,
  }: {
    id: string;
    label: string;
    isDraggable: boolean;
    isDragging: boolean;
    isColumnDragging: boolean;
    children: React.ReactNode;
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id,
      disabled: !isDraggable,
      animateLayoutChanges: () => false,
      transition: SORTABLE_TRANSITION,
    });

    return (
      <TableColumnsDraggableGroupContainer
        ref={setNodeRef}
        style={{ transform: CSS.Translate.toString(transform), transition }}
        isDragging={isDragging}
      >
        <TableColumnsGroup
          isNested
          label={
            <TableColumnsDraggableGroupLabel isDragging={isDragging}>
              {label}
            </TableColumnsDraggableGroupLabel>
          }
          isDragging={isColumnDragging}
          controls={
            isDraggable && (
              <TableColumnsGroupHandle
                {...attributes}
                {...listeners}
                active={isDragging}
                icon="slide-vertical"
                isDragging={isDragging}
                label={`Reorder ${label}`}
                variant={isDragging ? 'brand' : 'neutral'}
              />
            )
          }
        >
          {children}
        </TableColumnsGroup>
      </TableColumnsDraggableGroupContainer>
    );
  },
);

const TableColumnsInner = memo(
  ({
    columns,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    onResetColumnOrder,
    defaultColumnOrder,
  }: TableColumnsVisibilityProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const filteredColumns = useMemo(
      () =>
        columns.filter(
          (column) => column.id && column.meta?.accessLevel !== null && column.meta?.group !== null,
        ),
      [columns],
    );

    const groupedColumns = useMemo(() => {
      const groups = new Map<string, ColumnDef<any, any>[]>();

      for (const col of filteredColumns) {
        const groupName =
          col.meta?.group ??
          (col.meta?.accessLevel === AccessLevel.Internal
            ? OPERATOR_COLUMN_GROUP_NAME
            : DEFAULT_COLUMN_GROUP_NAME);

        let arr = groups.get(groupName);
        if (!arr) {
          arr = [];
          groups.set(groupName, arr);
        }

        arr.push(col);
      }

      return groups;
    }, [filteredColumns]);

    const sortedColumnGroups = useMemo(() => {
      if (!groupedColumns || groupedColumns.size === 0) return [];

      return Array.from(groupedColumns.entries()).sort(([groupA], [groupB]) => {
        if (groupA === DEFAULT_COLUMN_GROUP_NAME && groupB !== DEFAULT_COLUMN_GROUP_NAME) return -1;
        if (groupA !== DEFAULT_COLUMN_GROUP_NAME && groupB === DEFAULT_COLUMN_GROUP_NAME) return 1;

        if (groupA === OPERATOR_COLUMN_GROUP_NAME && groupB !== OPERATOR_COLUMN_GROUP_NAME)
          return 1;
        if (groupA !== OPERATOR_COLUMN_GROUP_NAME && groupB === OPERATOR_COLUMN_GROUP_NAME)
          return -1;

        return groupA.localeCompare(groupB);
      });
    }, [groupedColumns]);

    const sortedColumnsInGroups = useMemo(() => {
      if (!columnOrder) return sortedColumnGroups;

      return sortedColumnGroups.map(([groupName, groupColumns]) => {
        const sortedGroupColumns = groupColumns.sort((a, b) => {
          const aIndex = columnOrder.indexOf(a.id!);
          const bIndex = columnOrder.indexOf(b.id!);

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }

          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;

          return 0;
        });

        return [groupName, sortedGroupColumns] as [string, ColumnDef<any, any>[]];
      });
    }, [sortedColumnGroups, columnOrder]);

    const handleVisibilityChange = useCallback(
      (columnId: string, checked: boolean) => {
        const newVisibility = {
          ...columnVisibility,
          [columnId]: checked,
        };
        setColumnVisibility(newVisibility);
      },
      [columnVisibility, setColumnVisibility],
    );

    const handleResetColumnOrder = useCallback(() => {
      onResetColumnOrder?.();
    }, [onResetColumnOrder]);

    const isColumnOrderDefault = useMemo(() => {
      if (!defaultColumnOrder || !columnOrder) return true;

      if (defaultColumnOrder.length !== columnOrder.length) return false;

      return defaultColumnOrder.every((id, index) => id === columnOrder[index]);
    }, [defaultColumnOrder, columnOrder]);

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuButton
          appearance="muted"
          arrangement="hidden-label"
          icon="columns"
          variant="secondary"
          size="small"
        >
          Manage columns
        </DropdownMenuButton>
        <DropdownMenuPopover align="start">
          {sortedColumnsInGroups.map(([groupName, groupColumns]) => (
            <DropdownMenuGroup key={groupName} label={groupName}>
              {groupColumns.map((column) => {
                if (!column.id) return null;
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={columnVisibility[column.id] ?? true}
                    accessLevel={column.meta?.accessLevel}
                    closeOnClick={false}
                    onCheckedChange={(checked) => {
                      handleVisibilityChange(column.id!, checked);
                    }}
                  >
                    {column.meta?.label ??
                      (typeof column.header === 'string' ? column.header : column.id)}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuGroup>
          ))}
          {onResetColumnOrder && (
            <DropdownMenuGroup label="Controls">
              <DropdownMenuItem
                onClick={handleResetColumnOrder}
                disabled={isColumnOrderDefault}
                icon="reset"
              >
                Reset order
              </DropdownMenuItem>
            </DropdownMenuGroup>
          )}
        </DropdownMenuPopover>
      </DropdownMenu>
    );
  },
);

const isOperatorColumn = (column: ColumnDef<any, any>) =>
  column.meta?.accessLevel === AccessLevel.Internal;

const lockHorizontalMovement: Modifier = ({ transform }) => ({ ...transform, x: 0 });

type SortableColumnMenuEntry = {
  list: string;
  index: number;
  columnIDs: string[];
  isFixed: boolean;
};

// Enhanced component that works with TableState
function TableColumnsEnhancedComponent<D>({ columns, tableState }: TableColumnsEnhancedProps<D>) {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedID, setDraggedID] = useState<string | null>(null);
  const [tempColumnOrder, setTempColumnOrder] = useState<string[] | null>(null);

  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          column && column.id && column.meta?.accessLevel !== null && column.meta?.group !== null,
      ),
    [columns],
  );

  const columnOrder = tempColumnOrder ?? tableState.columnOrder;
  const { items, operatorColumns } = useMemo(
    () => buildColumnMenu(filteredColumns, columnOrder, isOperatorColumn),
    [filteredColumns, columnOrder],
  );

  const sortables = useMemo(() => {
    const entries = new Map<string, SortableColumnMenuEntry>();
    const addColumns = (list: string, listColumns: ColumnDef<any, any>[]) =>
      listColumns.forEach((col, index) => {
        if (!col.id) return;
        entries.set(col.id, {
          list,
          index,
          columnIDs: [col.id],
          isFixed: Boolean(col.meta?.fixedPosition),
        });
      });

    items.forEach((item, index) => {
      entries.set(item.id, {
        list: COLUMN_MENU_ROOT,
        index,
        columnIDs: columnMenuItemColumnIDs(item),
        isFixed:
          item.type === 'column'
            ? Boolean(item.column.meta?.fixedPosition)
            : item.columns.some((col) => col.meta?.fixedPosition),
      });
      if (item.type === 'group') addColumns(item.id, item.columns);
    });
    addColumns(COLUMN_MENU_OPERATOR, operatorColumns);

    return entries;
  }, [items, operatorColumns]);

  const draggedList = draggedID ? sortables.get(draggedID)?.list : undefined;

  // Columns and groups only trade places with siblings in the same list.
  const collisionDetection: CollisionDetection = useCallback(
    ({ active, collisionRect, droppableRects }) => {
      const from = sortables.get(String(active.id));
      if (!from) return [];

      const siblings = Array.from(droppableRects, ([id, rect]) => {
        const entry = sortables.get(String(id));
        return entry?.list === from.list && id !== active.id
          ? [{ id, index: entry.index, top: rect.top, height: rect.height }]
          : [];
      }).flat();

      return [{ id: findSortableOver(from.index, collisionRect, siblings) ?? active.id }];
    },
    [sortables],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setDraggedID(null);

      const from = sortables.get(String(active.id));
      const to = over ? sortables.get(String(over.id)) : undefined;
      if (!from || !to || from === to || from.list !== to.list || from.isFixed || to.isFixed) {
        return;
      }

      const newOrder = moveColumnIDs(
        tableState.columnOrder,
        from.columnIDs,
        to.columnIDs,
        from.index < to.index,
      );
      // Holds the new order until the table state round-trips, to prevent flicker.
      setTempColumnOrder(newOrder);
      tableState.setColumnOrder(newOrder);
      setTimeout(() => setTempColumnOrder(null), 100);
    },
    [sortables, tableState],
  );

  const handleVisibilityChange = useCallback(
    (columnId: string, checked: boolean) => {
      tableState.setColumnVisibility({
        ...tableState.columnVisibility,
        [columnId]: checked,
      });
    },
    [tableState],
  );

  const renderColumn = (column: ColumnDef<any, any>) =>
    column.id ? (
      <TableColumnsDraggable
        key={column.id}
        column={column}
        columnVisibility={tableState.columnVisibility as VisibilityState}
        onVisibilityChange={handleVisibilityChange}
        isDragging={draggedID === column.id}
      />
    ) : null;

  return filteredColumns.length > 0 ? (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuButton
        appearance="muted"
        arrangement="hidden-label"
        icon="columns"
        variant="secondary"
        size="small"
      >
        Manage columns
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" hasInnerScroll>
        <TableColumnsScrollArea>
          <DndContext
            collisionDetection={collisionDetection}
            modifiers={[lockHorizontalMovement]}
            onDragStart={(event) => setDraggedID(String(event.active.id))}
            onDragCancel={() => setDraggedID(null)}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableColumnsGroup
                label={DEFAULT_COLUMN_GROUP_NAME}
                isDragging={draggedList === COLUMN_MENU_ROOT}
              >
                {items.map((item) =>
                  item.type === 'column' ? (
                    renderColumn(item.column)
                  ) : (
                    <TableColumnsDraggableGroup
                      key={item.id}
                      id={item.id}
                      label={item.name}
                      isDraggable={!sortables.get(item.id)?.isFixed}
                      isDragging={draggedID === item.id}
                      isColumnDragging={draggedList === item.id}
                    >
                      <SortableContext
                        items={columnMenuItemColumnIDs(item)}
                        strategy={verticalListSortingStrategy}
                      >
                        {item.columns.map(renderColumn)}
                      </SortableContext>
                    </TableColumnsDraggableGroup>
                  ),
                )}
              </TableColumnsGroup>
            </SortableContext>
            <SortableContext
              items={operatorColumns.flatMap((col) => (col.id ? [col.id] : []))}
              strategy={verticalListSortingStrategy}
            >
              <TableColumnsGroup
                label={OPERATOR_COLUMN_GROUP_NAME}
                isDragging={draggedList === COLUMN_MENU_OPERATOR}
              >
                {operatorColumns.map(renderColumn)}
              </TableColumnsGroup>
            </SortableContext>
          </DndContext>
        </TableColumnsScrollArea>
        <DropdownMenuGroup label="Reset columns">
          <DropdownMenuItem
            onClick={tableState.resetColumnOrder}
            disabled={tableState.isColumnOrderDefault}
            icon="columns"
          >
            Order
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={tableState.resetColumnVisibility}
            disabled={tableState.isColumnVisibilityDefault}
            icon="eye-open"
          >
            Visibility
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  ) : null;
}

export const TableColumns = memo(TableColumnsInner);

export const TableColumnsEnhanced = memo(
  TableColumnsEnhancedComponent,
  // This typecast is to make the generics propagate properly
) as typeof TableColumnsEnhancedComponent;
